import { AD_POSITION } from "@/constants/ads";
import { MARK } from "@/constants/customPageSizeMark";
import { RENDERCARDTYPE } from "@/constants/renderCardType";
import useAppStore from "@/context/store";
import SearchEmpty from "@/pageComponents/Search/SearchEmpty";
import { getExpsValue } from "@/tracker/abTest";
import pvTracker from "@/tracker/report/pvTracker";
import getPageData from "@/tracker/shared/getPageData";
import { ConcurrencyController } from "@/utils/concurrencyController";
import filterUrlParams from "@/utils/filterUrlParams";
import { getPageSize } from "@/utils/getPageSize";
import { useMemoizedFn } from "ahooks";
import { uniqBy } from "lodash";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import PinList from "../../components/PinList";
import {
  getAds,
  getDeliveryDetail,
  getSearchPinsByQingzhou,
} from "../../services";
import { RelationWords } from "../Search/RelationWords";
import styles from "./index.module.less";
import RecallSearch from "./recallSearch";

export default function SearchPinList({
  query,
  searchResult,
  setRecommend,
  type,
  showMaterialTag = true,
}) {
  const pageRef = useRef(searchResult ? 2 : 1);
  const randomNumRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState(searchResult || []);
  const [isAllMatch, setIsAllMatch] = useState(true);
  const [relationList, setRelationList] = useState([]);
  const [isRecallSearch, setIsRecalSearch] = useState(false);
  const [splitWords, setSplitWords] = useState([]);
  const [ads, setAds] = useState([]);
  const [questionaire, setQuestionaire] = useState(null);
  const router = useRouter();
  const pathname = router.pathname;
  const initRef = useRef(!searchResult);
  const { user } = useAppStore(({ user }) => ({
    user,
  }));
  const { q, sort, filterIds } = query;
  const [busy, setBusy] = useState(false);
  const [extraPinQueue, setExtraPinQueue] = useState([]);

  const filterIdMap = useMemo(() => {
    let map = null;
    try {
      if (filterIds) {
        map = filterUrlParams.decode(filterIds);
      }
    } catch (e) {
      console.error("filter_ids对象转换失败");
      return {};
    }
    return map;
  }, [filterIds]);

  const category = useMemo(() => {
    return Object.values(filterIdMap || {}).reduce((result, value) => {
      if (result) {
        return result;
      } else {
        const match = value.match(/category:(.*)/);
        return match && match[1];
      }
    }, "");
  }, [filterIdMap]);

  const queryAction = useMemo(() => {
    // 服务端渲染时，不做初始化的一些操作
    if (initRef.current) {
      pageRef.current = 1;
      setHasMore(true);
      setDataSource([]);
    } else {
      initRef.current = true;
    }
    return async () => {
      if (!q) {
        return;
      }
      if (pageRef.current === 1) {
        setIsRecalSearch(false);
      }

      const filter_ids = filterIdMap
        ? Object.values(filterIdMap).reduce((result, value) => {
            result.push(value);
            return result;
          }, [])
        : null;

      // 设置如果超过30秒，显示服务器繁忙，请稍等片刻...
      const timer = setTimeout(() => {
        setBusy(true);
      }, 10000);

      const searchAb = await getExpsValue("search_ab", "old");

      const [result, waterfallAd] = await Promise.all([
        getSearchPinsByQingzhou({
          text: q,
          ...(sort ? { sort } : null),
          ...(filter_ids ? { filter_ids } : null),
          ...(pathname === "/topic-pin/[keyword]"
            ? { all_match: false }
            : null),
          colors: filterIdMap?.colors?.split(",").pop(),
          limit: getPageSize(MARK.SEARCHPIN),
          page: pageRef.current,
          position: "search_pin",
          // 控制联想词数据
          ...(searchAb === "new" ? { relations: 20 } : null),
        }),
        pageRef.current > 1 // 第一页不展示广告，故不请求
          ? getAds({
              positions: [AD_POSITION.SEARCH_WATERFALL_PER_PAGE],
              variables: {
                user_tag: ["pin"],
                category: [category || "none"],
                search_keyword: [q],
              },
            })
          : null,
      ]);

      // 如果结果返回有问题直接设置繁忙，如果没问题就清楚定时器
      if (result?.err) {
        setBusy(true);
      } else {
        clearTimeout(timer);
        setBusy(false);
      }

      const pins = result?.pins || [];

      // 搜索推荐主题， 仅在第一页请求更新数据
      if (pageRef.current === 1) {
        setRecommend(result.rec_topic_material);
        setRelationList(result.relations || []);
        if (result.rec_topic_material?.recommendType === "material") {
          setExtraPinQueue(result.rec_topic_material.recommendMaterial);
        }
      }

      if (waterfallAd?.[0] && pins.length > 0) {
        //追加到列表前
        pins.unshift({
          ...waterfallAd[0],
          pin_id: `recommend-${waterfallAd[0].material.id}-${pageRef.current}`,
          type: "recommend",
        });
      }

      /**
       * 展示部分匹配的情况
       * 1.素材数量<=30分词数>1,且用户登陆了
       * 2.素材数量<=30分词数>1,用户没登陆，且素材数量为0
       */
      if (
        result.total <= 30 &&
        result.split_words?.length > 1 &&
        // 使用userid是为了避免user对象改变导致搜索结果页刷新
        (user?.user_id || (!user?.user_id && !pins.length))
      ) {
        setIsRecalSearch(true);
        // setHasMore(false);
        setSplitWords(result.split_words);
      }
      if (pins.length <= 0) {
        setHasMore(false);
      }

      // 搜索结果也第N屏后插入主题
      if (result.topics) {
        const recommendTopics = result.topics
          .map((item) => {
            return {
              ...item,
              pin_id: `topic_${item.filter_id}`,
              type: RENDERCARDTYPE.topicCollection,
            };
          })
          // 把没有采集的过滤掉，避免展示异常
          .filter((item) => item?.pins?.length > 0);

        if (recommendTopics[0]) {
          // 第一个放到最前面
          pins.unshift(recommendTopics[0]);
        }

        if (recommendTopics[1]) {
          //第二个放到中间位置
          pins.splice(Math.ceil(pins.length / 2), 0, recommendTopics[1]);
        }
      }

      setDataSource((dataSource) => dataSource.concat(pins));
    };
  }, [q, filterIdMap, sort, pathname, user?.user_id, setRecommend]);

  const queryRecommend = useMemo(() => {
    return async () => {
      const result = await getAds({
        positions: [
          AD_POSITION.SEARCH_WATERFALL_FIXED_1,
          AD_POSITION.SEARCH_WATERFALL_FIXED_2,
          AD_POSITION.SEARCH_WATERFALL_RANDOM_1,
          AD_POSITION.SEARCH_WATERFALL_4,
        ],
        variables: {
          user_tag: ["pin"],
          category: [category || "none"],
          search_keyword: [q],
        },
      });

      setAds(
        result?.reduce((result, item) => {
          if (item) {
            result.push({
              ...item,
              pin_id: `recommend-${item.material.id}`,
              type: "recommend",
            });
          } else {
            result.push(null);
          }
          return result;
        }, []) || []
      );
    };
  }, [category, q]);

  const queryQuestionaire = useMemo(() => {
    return async () => {
      const result = await getDeliveryDetail("web-search-questionaire");
      setQuestionaire(
        result?.pit_modules?.[0]?.material
          ? {
              type: RENDERCARDTYPE.questionaire,
              pin_id: `questionaire-${result?.pit_modules?.[0]?.module_id}`,
              id: result?.pit_modules?.[0]?.module_id,
              name: result?.pit_modules?.[0]?.module_name,
              search_word: q,
              ...result?.pit_modules?.[0]?.material,
            }
          : null
      );
    };
  }, [q]);

  // 创建串行任务队列
  const queryTask = useRef(new ConcurrencyController(1));

  useEffect(() => {
    queryTask.current.run(queryAction);
  }, [queryAction]);

  useEffect(() => {
    queryRecommend();
  }, [queryRecommend]);

  useEffect(() => {
    queryQuestionaire();
  }, [queryQuestionaire]);

  const onLoadMore = useMemoizedFn(async () => {
    queryTask.current.run(async () => {
      pageRef.current += 1;
      await queryAction();
      pvTracker(getPageData());
    });
  });

  const list = useMemo(() => {
    if (dataSource.length <= 0 || (ads.length <= 0 && !questionaire)) {
      return dataSource;
    }

    if (randomNumRef.current === null) {
      // 随机 11-20 的位置
      randomNumRef.current = parseInt(Math.random() * 10) + 11;
    }

    const num = randomNumRef.current;

    const list = [
      ...ads.slice(0, 1).filter((i) => i),
      ...dataSource.slice(0, num),
    ];

    const newList1 = [
      ...list.slice(0, 1),
      ...ads.slice(1, 2).filter((i) => i),
      ...list.slice(1),
      ...ads.slice(2, 3).filter((i) => i),
      ...dataSource.slice(num),
    ];

    const newList2 = [
      ...newList1.slice(0, 3),
      ...ads.slice(3, 4).filter((i) => i),
      ...newList1.slice(3),
    ];

    if (newList2.length > 60 && questionaire) {
      newList2.splice(60, 0, questionaire);
    }

    return uniqBy(newList2, "pin_id");
  }, [dataSource, ads, questionaire]);

  return (
    <div className={styles.pin} data-search-word={q}>
      <If condition={dataSource.length > 0 || hasMore}>
        <Then>
          <When condition={type === "pin" && !!relationList?.length}>
            <RelationWords relationList={relationList} />
          </When>
          {/* 用来控制pinQueue的值， 控制详情弹窗的下一张取值正确        
            1.如果用户点击全匹配的则pinQueue为全匹配的
            2.如果用户点击部分匹配的则pinQueue的数据为部分匹配
          */}
          <div
            onClick={() => {
              setIsAllMatch(true);
            }}
          >
            <PinList
              isSearch={true}
              searchWord={router.query?.q}
              dataSource={list}
              hasMore={hasMore}
              onLoadMore={onLoadMore}
              loadingContent={
                busy ? (
                  <div className={styles.busy}>服务器繁忙，请稍等片刻...</div>
                ) : null
              }
              endMessage={isRecallSearch ? "" : undefined}
              disableQueue={!isAllMatch}
              showMaterialTag={showMaterialTag}
              extraPinQueue={extraPinQueue}
            />
          </div>
        </Then>
        <Else>
          {!isRecallSearch && (
            <div style={{ marginTop: "48px" }}>
              <SearchEmpty></SearchEmpty>
            </div>
          )}
        </Else>
      </If>
      {/* abtest 基于部分匹配召回搜索的前端交互优化 */}
      <When condition={isRecallSearch && !hasMore}>
        <RecallSearch
          splitWords={splitWords}
          query={query}
          isAllMatch={isAllMatch}
          haveData={!!dataSource.length}
          type={type}
          setIsAllMatch={setIsAllMatch}
          dataSource={dataSource}
        ></RecallSearch>
      </When>
    </div>
  );
}
